import { createContext, useContext, useState } from 'react';

import { FCWithChildren } from 'common/types';

import ArchiveModal, { ArchiveModalProps } from './components/ArchiveModal';
import ConvertQuoteToInvoiceModal, {
  ConvertQuoteToInvoiceModalProps,
} from './components/ConvertQuoteToInvoiceModal';
import DeleteModal, { DeleteModalProps } from './components/DeleteModal';
import DownloadModal, { DownloadModalProps } from './components/DownloadModal';
import DuplicateModal, {
  DuplicateModalProps,
} from './components/DuplicateModal';
import MarkAsAcceptedModal, {
  MarkAsAcceptedProps,
} from './components/MarkAsAcceptedModal';
import MarkAsSentModal, {
  MarkAsSentModalProps,
} from './components/MarkAsSentModal';
import ResendByMyselfModal, {
  ResendByMyselfModalProps,
} from './components/ResendByMyselfModal';
import ResendWithShineModal, {
  ResendWithShineModalProps,
} from './components/ResendWithShineModal';
import ReviseModal, { ReviseModalProps } from './components/ReviseModal';
import SendModal, { SendModalProps } from './components/SendModal';

type Modal =
  | {
      name: 'archive';
      props: ArchiveModalProps;
    }
  | {
      name: 'convert-quote-to-invoice';
      props: ConvertQuoteToInvoiceModalProps;
    }
  | {
      name: 'delete';
      props: DeleteModalProps;
    }
  | {
      name: 'download';
      props: DownloadModalProps;
    }
  | {
      name: 'duplicate';
      props: DuplicateModalProps;
    }
  | {
      name: 'mark-as-accepted';
      props: MarkAsAcceptedProps;
    }
  | {
      name: 'mark-as-sent';
      props: MarkAsSentModalProps;
    }
  | {
      name: 'resend-by-myself';
      props: ResendByMyselfModalProps;
    }
  | {
      name: 'resend-with-shine';
      props: ResendWithShineModalProps;
    }
  | {
      name: 'revise';
      props: ReviseModalProps;
    }
  | {
      name: 'send';
      props: SendModalProps;
    };

interface ContextType {
  openModal: (props: Modal) => void;
  hideModal: () => void;
}

const QuoteModalsContext = createContext<ContextType>({
  hideModal: () => {},
  openModal: () => {},
});

export const QuoteModalsProvider: FCWithChildren = ({ children }) => {
  const [modal, setModal] = useState<Modal | null>(null);

  return (
    <QuoteModalsContext.Provider
      value={{
        hideModal: () => setModal(null),
        openModal: setModal,
      }}
    >
      {children}

      {modal?.name === 'archive' ? <ArchiveModal {...modal.props} /> : null}

      {modal?.name === 'convert-quote-to-invoice' ? (
        <ConvertQuoteToInvoiceModal {...modal.props} />
      ) : null}

      {modal?.name === 'delete' ? <DeleteModal {...modal.props} /> : null}
      {modal?.name === 'download' ? <DownloadModal {...modal.props} /> : null}
      {modal?.name === 'duplicate' ? <DuplicateModal {...modal.props} /> : null}

      {modal?.name === 'mark-as-accepted' ? (
        <MarkAsAcceptedModal {...modal.props} />
      ) : null}

      {modal?.name === 'mark-as-sent' ? (
        <MarkAsSentModal {...modal.props} />
      ) : null}

      {modal?.name === 'resend-by-myself' ? (
        <ResendByMyselfModal {...modal.props} />
      ) : null}

      {modal?.name === 'resend-with-shine' ? (
        <ResendWithShineModal {...modal.props} />
      ) : null}

      {modal?.name === 'revise' ? <ReviseModal {...modal.props} /> : null}
      {modal?.name === 'send' ? <SendModal {...modal.props} /> : null}
    </QuoteModalsContext.Provider>
  );
};

export const useQuoteModals = () => {
  const context = useContext(QuoteModalsContext);

  if (!context) {
    throw new Error(
      'useQuoteModals can only be used inside of a QuoteModalsProvider',
    );
  }

  return context;
};
